<template>
  <Teleport :to="portalRootElementSelector">
    <template v-if="keepTreeAlive">
      <div
        ref="modalRef"
        class="flex items-center justify-center group"
        :class="
          isOpen ? 'fixed inset-0 !z-[10000] modal-open' : 'invisible hidden'
        "
        role="dialog"
        aria-modal="true"
        v-bind="$attrs"
        :aria-hidden="!isOpen"
      >
        <button
          v-if="!preventCloseOnBackdropClick"
          class="absolute inset-0 bg-black bg-opacity-45 content-none backdrop -z-[1]"
          tabindex="-1"
          aria-label="Close modal"
          @click="!preventCloseOnBackdropClick && close()"
        ></button>
        <div
          v-else
          class="absolute inset-0 bg-black bg-opacity-45 content-none backdrop -z-[1]"
        />

        <slot v-if="$slots.body" name="body" :close="close" :is-open="isOpen" />

        <template v-else>
          <ModalBody :is-open="isOpen" @close="close">
            <slot />
          </ModalBody>
        </template>
      </div>
    </template>
    <Transition v-else name="modal" mode="out-in">
      <div
        v-if="isOpen"
        ref="modalRef"
        class="modal-open fixed inset-0 !z-[10000] flex items-center justify-center group"
        role="dialog"
        aria-modal="true"
        v-bind="$attrs"
        :aria-hidden="!isOpen"
      >
        <button
          v-if="!preventCloseOnBackdropClick"
          class="absolute inset-0 bg-black bg-opacity-45 content-none backdrop -z-[1]"
          tabindex="-1"
          aria-label="Close modal"
          @click="!preventCloseOnBackdropClick && close()"
        ></button>
        <div
          v-else
          class="absolute inset-0 bg-black bg-opacity-45 content-none backdrop -z-[1]"
        />

        <slot v-if="$slots.body" name="body" :close="close" :is-open="isOpen" />

        <template v-else>
          <ModalBody :is-open="isOpen" @close="close">
            <slot />
          </ModalBody>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useTemplateRef, watch, watchEffect } from "vue";
import ModalBody from "./modal-body.vue";
import * as focusTrap from "focus-trap";
import { portalRootElementId, portalRootElementSelector } from "@/constants/ui";
import { nextEventLoopTick } from "@/utils/reactivity";
import { useAppStore } from "@/stores/app.store";
import { sendEmbedModalState } from "@/composables/auth/use-embed-authentication-broker";

defineOptions({
  inheritAttrs: false,
});

const isOpen = defineModel<boolean>({
  required: true,
});
const emit = defineEmits<{
  close: [];
}>();
const _props = defineProps<{
  id?: string;
  keepTreeAlive?: boolean;
  preventCloseOnBackdropClick?: boolean;
}>();

const modalRef = useTemplateRef("modalRef");
const appStore = useAppStore();

// Notify the host SDK so it can lock scrolling when auto-height embed
// is active — otherwise the modal overlay stretches the iframe but the
// host page stays scrollable and the modal isn't centred in the viewport.
if (appStore.isEmbed) {
  watch(isOpen, (open) => sendEmbedModalState(open));
}

function close() {
  emit("close");
  isOpen.value = false;
}

watchEffect(async (onCleanup) => {
  const element = modalRef.value;
  if (!element || !isOpen.value) return;

  // Give time for the modal to be rendered before activating the focus trap
  await nextEventLoopTick();

  const portalsRoot = document.getElementById(portalRootElementId)!;
  // Enable focus on elements within the modal and to any teleported element
  // (e.g. dropdowns, combobox, select, etc.)
  const trap = focusTrap.createFocusTrap([element, portalsRoot], {
    escapeDeactivates: false, // We handle Escape ourselves to also close the modal
  });
  trap.activate();

  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  }
  document.addEventListener("keydown", onKeydown);

  onCleanup(() => {
    document.removeEventListener("keydown", onKeydown);
    trap.deactivate();
  });
});
</script>

<style scoped>
.modal-enter-active {
  animation: all 0.5s;
  transition: all 0.5s;
}

.modal-leave-active {
  /* animation: none 0.5s; */
  transition: opacity 100ms ease-out;
}

.modal-leave-from {
  opacity: 1;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-active * {
  animation: zoomIn 150ms;
}

.modal-enter-active .backdrop {
  animation: fadeIn 100ms;
}
</style>
